import type { WhiteLabel } from '@skytech/types'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import useUser from '@/hooks/user'
import type { UserRole } from '@/types'
import { APP_ROUTES } from '@/util/app-routes'

export const USER_ROLE: Record<string, UserRole> = {
  ADMIN: 'admin',
  CUSTOMER_HEAD_ADMIN: 'customer_head_admin',
  CUSTOMER_ADMIN: 'customer_admin',
  DEPARTMENT_HEAD_CUSTOMER_HEAD: 'department_head_corp',
  DEPARTMENT_HEAD: 'department_head',
  REPORT_USER: 'report_user',
  IT_USER: 'it_user',
}

export const ALL_ROLES = Object.values(USER_ROLE)

export const ADMIN_ROLES = [
  USER_ROLE.ADMIN,
  USER_ROLE.CUSTOMER_HEAD_ADMIN,
  USER_ROLE.CUSTOMER_ADMIN,
]

export const DEPARTMENT_ROLES = [USER_ROLE.DEPARTMENT_HEAD_CUSTOMER_HEAD, USER_ROLE.DEPARTMENT_HEAD]

export const useAccess = (roles: Partial<UserRole>[]) => {
  const navigate = useNavigate()
  const role = useUser()?.role

  if (!role) {
    navigate(APP_ROUTES.HOME)
    return
  }

  !(roles || []).includes(role) && navigate(APP_ROUTES.HOME)
}

export const useAdminAccess = () => useAccess(ADMIN_ROLES)

export const useHasRoleBasedAccess = (roles: Partial<UserRole>[]) => {
  const user = useUser()

  if (!user) return false

  return roles.includes(user.role)
}

export const useIsAdmin = () => {
  const user = useUser()

  if (!user) return false

  return ADMIN_ROLES.includes(user.role)
}

export const useHasGlobalAccess = () => {
  const user = useUser()

  if (!user) return false

  return [USER_ROLE.ADMIN, USER_ROLE.CUSTOMER_HEAD_ADMIN].includes(user.role)
}

export const useWhiteLabelBasedAccess = (whiteLabelList: WhiteLabel[]) => {
  const navigate = useNavigate()
  const { whiteLabel: currentWhiteLabel } = useAppContext()

  if (whiteLabelList.includes(currentWhiteLabel)) {
    navigate(APP_ROUTES.HOME)
    return
  }
}

export const useIsSkytechWL = () => {
  const { whiteLabel } = useAppContext()
  if (!whiteLabel) return false

  const skytech = ['skytech', 'control', 'tcm']
  return skytech.includes(whiteLabel)
}

export const useIsThreeStepIT = () => {
  const { whiteLabel } = useAppContext()

  if (!whiteLabel) return false

  return whiteLabel === 'threeStepIT'
}

export const useIsTelia = () => {
  const { whiteLabel } = useAppContext()

  if (!whiteLabel) return false

  return whiteLabel === 'telia'
}
